import React, { useEffect, useState } from "react";
import { fmtMonth } from "@/lib/dt";
import api from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AILabel from "@/components/AILabel";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { toast } from "sonner";
import LayerLine from "@/components/LayerLine";   // Batch 32 · B32-1 — the layer sentence (was the LayerBadge pill)
import StoredResultBanner from "@/components/StoredResultBanner";   // Batch 27 · Add-on 2 Step 2
import { Sparkles, TrendingUp, TrendingDown, AlertTriangle, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { fmtDate } from "@/lib/dt";
import { RestatementClientDataNote } from "@/components/restatement/RestatementViews";   // Prior-Period Restatement · Phase 3b
import TbOnlyNotice, { isTbOnly } from "@/components/TbOnlyNotice";   // PR 3 · rule 1 (B-1a): TB-only → the journal-line parts "not applicable"
import TbOnlyPart, { partNa } from "@/pages/Analytics/TbOnlyPart";   // STEP 2 · A9: each journal-line card / chart says so itself
import { fmtNumber } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter
import PeriodEndNote from "@/components/PeriodEndNote";   // STEP 3 · B-7b

// Pill shortcut that jumps into Sampling with Continuous method + this rule
// pre-selected. Detection stays here in Analytics — the shortcut is just
// navigation, matching the Fraud Fingerprints "Test these now" pattern.
function TestTheseNowPill({ rule, testid }) {
  const { t } = useI18n();
  return (
    <Link to={`/sampling?method=continuous&rule=${encodeURIComponent(rule)}`}
          className="mt-2 inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300"
          data-testid={testid}
          title={t("analyticsExtras.testTheseNowTitle")}>
      <Play className="h-2.5 w-2.5" /> {t("analyticsExtras.testTheseNow")}
    </Link>
  );
}

export default function Analytics() {
  const { t, lang } = useI18n();
  const { current } = useEngagement();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("empty");
  const [generatedAt, setGeneratedAt] = useState("");
  const [runState, setRunState] = useState(null);   // Batch 27 · Add-on 2 Step 2: the shared run state
  const [scanning, setScanning] = useState(false);
  const [obs, setObs] = useState([]);
  const [aiUsed, setAiUsed] = useState(true);   // 5c · F-2
  const [obsLoading, setObsLoading] = useState(false);
  const pollTimerRef = React.useRef(null);

  const engId = current?.id;
  const fetchOnce = React.useCallback(async () => {
    if (!engId) return;
    try {
      const r = await api.get(`/analytics/${engId}`, { _silent: true });
      const d = r?.data;
      setStatus(d?.status || "empty");
      setGeneratedAt(d?.generated_at || "");
      setRunState(d?.run_state || null);
      if (d?.result) setData(d.result);
      if (d?.status !== "running") {
        setScanning(false);
        if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
      }
    } catch { /* keep quiet */ }
  }, [engId]);

  useEffect(() => {
    fetchOnce();
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, [current?.id, fetchOnce]);

  const runAnalyticsScan = async () => {
    if (!current) return;
    setScanning(true);
    try {
      const r = await api.post(`/analytics/${current.id}/scan`);
      if (r?.data?.status === "already_running") {
        toast.info(t("analyticsExtras.recomputeRunning"));
      } else {
        toast.success(t("analyticsExtras.recomputeQueued"));
      }
      setStatus("running");
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = setInterval(fetchOnce, 3000);
    } catch (e) {
      setScanning(false);
      toast.error(t("analyticsExtras.recomputeFailed"));
    }
  };

  const generateObs = async () => {
    if (!current) return;
    setObsLoading(true);
    try {
      const r = await api.post(`/analytics/${current.id}/observations`);
      setObs(r.data.observations || []);
      setAiUsed(r.data.ai_used !== false);            // 5c · F-2 (L28)
      if (r.data.ai_used === false) toast.info(t("analyticsExtras.obsStatistical"));
      else toast.success(t("analyticsExtras.obsGenerated"));
    } catch (e) {
      toast.error(t("analyticsExtras.obsFailed"));
    } finally {
      setObsLoading(false);
    }
  };

  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;
  if (!data && status !== "running" && status !== "empty" && status !== "stale") {
    return <div className="text-sm text-muted-foreground">{t("common.loading")}</div>;
  }
  if (!data) {
    return (
      <div className="space-y-6" data-testid="analytics-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t("analytics.title")}</h1>
            <LayerLine screen="analytics" />
            <RestatementClientDataNote engId={current?.id} screen="analytics" />
            <p className="text-sm text-muted-foreground">{t("analytics.subtitle")}</p>
          </div>
        </div>
        <StoredResultBanner lang={lang} state={runState?.state || "not_run"} runAt={runState?.run_at} runBy={runState?.run_by_name}
                            running={scanning || status === "running"} onRun={runAnalyticsScan} runTestId="analytics-run-btn"
                            testid="analytics-empty" />
      </div>
    );
  }

  const rev = data.revenue, exp = data.expenses, bs = data.balance_sheet;
  // Round 33 · C1 — one source per engagement, shown on the page; engagement currency.
  const ccy = data.currency || current?.currency || "";
  const srcLabel = data.source === "gl" ? t("analytics.sourceGl") : data.source === "tb" ? t("analytics.sourceTb") : t("analytics.sourceNone");
  const money = (n) => `${ccy ? ccy + " " : ""}${fmtNumber(Number(n || 0))}`;
  const na = (section, key) => partNa(data, section, key);   // STEP 2 · A9: a journal-line part on a TB-only engagement

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("analytics.title")}</h1>
          <LayerLine screen="analytics" />
          <PeriodEndNote engagement={current} />
          <RestatementClientDataNote engId={current?.id} screen="analytics" />
          <p className="text-sm text-muted-foreground">
            {t("analytics.subtitle")}
            <span className="ms-2 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-800" data-testid="analytics-source">
              {t("analytics.sourceLabel")}: {srcLabel}
            </span>

          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={generateObs} disabled={obsLoading} className="bg-violet-600 hover:bg-violet-700" data-testid="gen-obs-btn">
            <Sparkles className="me-1 h-4 w-4" />
            {obsLoading ? t("common.loading") : t("analytics.genObs")}
          </Button>
        </div>
      </div>

      <StoredResultBanner lang={lang} state={runState?.state || (status === "stale" ? "stale" : "fresh")} runAt={runState?.run_at || generatedAt}
                          runBy={runState?.run_by_name} running={scanning || status === "running"} onRun={runAnalyticsScan}
                          runTestId="analytics-refresh-btn" testid="analytics-status" />
      <TbOnlyNotice status={data} part="analytics" testid="analytics-tb-only" />

      {obs.length > 0 && (
        <Card className="border-violet-200 bg-violet-50/40 dark:bg-violet-950/20 dark:border-violet-900">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">{aiUsed ? t("analytics.obsTitle") : t("analytics.obsTitleStat")}</CardTitle>
              {aiUsed && <AILabel />}
            </div>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {obs.map((o, i) => (
                <li key={i} className="flex gap-2" data-testid={`obs-${i}`}>
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-violet-500" />
                  <span>{o}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="revenue">
        <TabsList>
          <TabsTrigger value="revenue" data-testid="tab-revenue">{t("analytics.revenue")}</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">{t("analytics.expenses")}</TabsTrigger>
          <TabsTrigger value="balance" data-testid="tab-balance">{t("analytics.balance")}</TabsTrigger>
        </TabsList>

        <TabsContent value="revenue" className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card><CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("common.total")}</div>
              <div className="mt-2 text-2xl font-bold num-cell">{money(rev.total)}</div>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("analytics.yoy")}</div>
              <div className="mt-2 flex items-center gap-2 text-2xl font-bold">
                {rev.yoy_pct == null ? <span className="text-sm font-normal text-muted-foreground" data-testid="analytics-no-py">{t("analytics.noPriorYear")}</span> : (<>
                  {rev.yoy_pct > 0 ? <TrendingUp className="h-5 w-5 text-emerald-600" /> : <TrendingDown className="h-5 w-5 text-red-600" />}
                  <span className={rev.yoy_pct > 0 ? "text-emerald-600" : "text-red-600"}>{rev.yoy_pct}%</span>
                </>)}
              </div>
              {rev.prior_year_total != null && (
                <div className="mt-1 text-xs text-muted-foreground" data-testid="analytics-py-total">
                  {t("analytics.priorYearTotal")}: {money(rev.prior_year_total)}
                </div>
              )}
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("analytics.cutoff")}</div>
              <TbOnlyPart na={na("revenue", "cutoff_entry_ids")} testid="analytics-na-cutoff">
                <div className="mt-2 text-2xl font-bold">{rev.cutoff_entry_ids.length}</div>
                {rev.cutoff_window?.from && (
                  <div className="mt-1 text-xs text-muted-foreground" data-testid="analytics-cutoff-window">
                    {t("s3r7.analyticsCutoffWindow").replace("{days}", String(rev.cutoff_window.days_before))
                      .replace("{from}", fmtDate(rev.cutoff_window.from)).replace("{to}", fmtDate(rev.cutoff_window.to))}
                  </div>
                )}
                <AILabel className="mt-2" />
                <TestTheseNowPill rule="Cut-off flag" testid="analytics-test-cutoff" />
              </TbOnlyPart>
            </CardContent></Card>
          </div>
          <Card>
            <CardHeader><CardTitle className="text-base">{t("analytics.monthly")}</CardTitle></CardHeader>
            {na("revenue", "monthly") ? (
              <CardContent><TbOnlyPart na testid="analytics-na-monthly" /></CardContent>
            ) : (
            <CardContent className="h-72 min-h-[288px]">
              <ResponsiveContainer width="100%" height="100%" minHeight={240}>
                <LineChart data={rev.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" fontSize={11} tickFormatter={fmtMonth} />
                  <YAxis fontSize={11} tickFormatter={(v) => fmtNumber(Number(v), { decimals: 0 })} />
                  <Tooltip />
                  <Line type="monotone" dataKey="amount" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
            )}
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">{t("analytics.topCustomers")}</CardTitle></CardHeader>
            {na("revenue", "top_customers") ? (
              <CardContent><TbOnlyPart na testid="analytics-na-top-customers" /></CardContent>
            ) : (
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-start">{t("analyticsExtras.customer")}</th>
                    <th className="px-4 py-2 text-end">{t("common.amount")}</th>
                    <th className="px-4 py-2 text-end">{t("analytics.concentration")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rev.top_customers.map((c, i) => (
                    <tr key={i} className="border-b">
                      <td className="px-4 py-2 font-medium">{c.name}</td>
                      <td className="px-4 py-2 num-cell">{fmtNumber(Number(c.amount || 0))}</td>
                      <td className="px-4 py-2 num-cell">
                        <span className={c.flag_high_concentration ? "font-semibold text-red-600" : ""}>{c.concentration_pct}%</span>
                        {c.flag_high_concentration && (
                          <span className="ms-2 rounded-md bg-red-50 px-2 py-0.5 text-xs text-red-700">{t("analyticsExtras.concentrationHigh")}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="expenses" className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
            <Card><CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("common.total")}</div>
              <div className="mt-2 text-2xl font-bold num-cell">{money(exp.total)}</div>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("analytics.benfordScore")}</div>
              <TbOnlyPart na={na("expenses", "benford")} testid="analytics-na-benford-score">
                <div className="mt-2 text-2xl font-bold num-cell">{exp.benford_deviation_score}</div>
                <AILabel className="mt-2" />
                <TestTheseNowPill rule="Benford deviation flag" testid="analytics-test-benford" />
              </TbOnlyPart>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("analytics.duplicates")}</div>
              <TbOnlyPart na={na("expenses", "duplicates")} testid="analytics-na-duplicates">
                <div className="mt-2 text-2xl font-bold">{exp.duplicates.length}</div>
              </TbOnlyPart>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("analytics.weekend")}</div>
              <TbOnlyPart na={na("expenses", "weekend_entry_ids")} testid="analytics-na-weekend">
                <div className="mt-2 text-2xl font-bold">{exp.weekend_entry_ids.length}</div>
              </TbOnlyPart>
            </CardContent></Card>
          </div>
          <Card><CardContent className="flex flex-wrap items-center gap-3 p-4">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("analyticsExtras.thresholdTitle")}</div>
              <div className="mt-1 text-xs text-muted-foreground">
                {t("analyticsExtras.thresholdDesc")}
              </div>
            </div>
            {/* STEP 2 · A9: the threshold rule tests single journal lines — no button on a TB-only engagement */}
            <TbOnlyPart na={isTbOnly(data)} testid="analytics-na-threshold">
              <TestTheseNowPill rule="Threshold breach" testid="analytics-test-threshold" />
            </TbOnlyPart>
          </CardContent></Card>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">{t("analytics.momVariance")}</CardTitle></CardHeader>
              {na("expenses", "mom_variance") ? (
              <CardContent><TbOnlyPart na testid="analytics-na-mom" /></CardContent>
            ) : (
              <CardContent className="h-72 min-h-[288px]">
                <ResponsiveContainer width="100%" height="100%" minHeight={240}>
                  <BarChart data={exp.mom_variance}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" fontSize={11} tickFormatter={fmtMonth} />
                    <YAxis fontSize={11} tickFormatter={(v) => fmtNumber(Number(v), { decimals: 0 })} />
                    <Tooltip />
                    <Bar dataKey="amount" fill="hsl(var(--chart-2))" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            )}
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">{t("analytics.benford")}</CardTitle></CardHeader>
              {na("expenses", "benford") ? (
              <CardContent><TbOnlyPart na testid="analytics-na-benford" /></CardContent>
            ) : (
              <CardContent className="h-72 min-h-[288px]">
                <ResponsiveContainer width="100%" height="100%" minHeight={240}>
                  <BarChart data={exp.benford}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="digit" fontSize={11} />
                    <YAxis fontSize={11} tickFormatter={(v) => fmtNumber(Number(v), { decimals: 0 })} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="expected" fill="hsl(var(--chart-2))" name={t("analyticsExtras.expectedPct")} />
                    <Bar dataKey="actual" fill="hsl(var(--chart-1))" name={t("analyticsExtras.actualPct")} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            )}
            </Card>
          </div>
          {exp.duplicates.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> {t("analytics.duplicates")}</CardTitle></CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-start">{t("analyticsExtras.vendor")}</th>
                      <th className="px-4 py-2 text-end">{t("common.amount")}</th>
                      <th className="px-4 py-2 text-start">{t("analyticsExtras.dates")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exp.duplicates.slice(0, 10).map((d, i) => (
                      <tr key={i} className="border-b">
                        <td className="px-4 py-2 font-medium">{d.vendor}</td>
                        <td className="px-4 py-2 num-cell">{fmtNumber(Number(d.amount || 0))}</td>
                        <td className="px-4 py-2">{d.dates.join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="balance" className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">{t("analytics.aging")} {t("analyticsExtras.receivablesSuffix")}</CardTitle></CardHeader>
              {na("balance_sheet", "receivables_aging") ? (
              <CardContent><TbOnlyPart na testid="analytics-na-ar-aging" /></CardContent>
            ) : (
              <CardContent className="h-64 min-h-[256px]">
                <ResponsiveContainer width="100%" height="100%" minHeight={220}>
                  <BarChart data={bs.receivables_aging}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="bucket" fontSize={11} />
                    <YAxis fontSize={11} tickFormatter={(v) => fmtNumber(Number(v), { decimals: 0 })} />
                    <Tooltip />
                    <Bar dataKey="amount" fill="hsl(var(--chart-1))" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            )}
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">{t("analytics.aging")} {t("analyticsExtras.payablesSuffix")}</CardTitle></CardHeader>
              {na("balance_sheet", "payables_aging") ? (
              <CardContent><TbOnlyPart na testid="analytics-na-ap-aging" /></CardContent>
            ) : (
              <CardContent className="h-64 min-h-[256px]">
                <ResponsiveContainer width="100%" height="100%" minHeight={220}>
                  <BarChart data={bs.payables_aging}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="bucket" fontSize={11} />
                    <YAxis fontSize={11} tickFormatter={(v) => fmtNumber(Number(v), { decimals: 0 })} />
                    <Tooltip />
                    <Bar dataKey="amount" fill="hsl(var(--chart-3))" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            )}
            </Card>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">{t("analytics.dormant")}</CardTitle></CardHeader>
              {na("balance_sheet", "dormant_accounts") ? (
                <CardContent><TbOnlyPart na testid="analytics-na-dormant" /></CardContent>
              ) : (
              <CardContent className="p-0 max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground"><tr>
                    <th className="px-3 py-2 text-start">{t("analyticsExtras.account")}</th>
                    <th className="px-3 py-2 text-end">{t("analyticsExtras.balance")}</th>
                    <th className="px-3 py-2 text-start">{t("analyticsExtras.lastDate")}</th>
                  </tr></thead>
                  <tbody>
                    {bs.dormant_accounts.length === 0 && <tr><td colSpan={3} className="px-3 py-4 text-muted-foreground text-center">—</td></tr>}
                    {bs.dormant_accounts.map((d, i) => (
                      <tr key={i} className="border-b">
                        <td className="px-3 py-2">{d.account_name}</td>
                        <td className="px-3 py-2 num-cell">{fmtNumber(Number(d.balance || 0))}</td>
                        <td className="px-3 py-2">{fmtDate(d.last_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
              )}
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">{t("analytics.negative")}</CardTitle></CardHeader>
              <CardContent className="p-0 max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground"><tr>
                    <th className="px-3 py-2 text-start">{t("analyticsExtras.account")}</th>
                    <th className="px-3 py-2 text-end">{t("analyticsExtras.balance")}</th>
                    <th className="px-3 py-2 text-start">{t("analyticsExtras.reason")}</th>
                  </tr></thead>
                  <tbody>
                    {bs.negative_balance_flags.length === 0 && <tr><td colSpan={3} className="px-3 py-4 text-muted-foreground text-center">—</td></tr>}
                    {bs.negative_balance_flags.map((n, i) => (
                      <tr key={i} className="border-b">
                        <td className="px-3 py-2">{n.account_name}</td>
                        <td className="px-3 py-2 num-cell text-red-600">{fmtNumber(Number(n.balance || 0))}</td>
                        <td className="px-3 py-2 text-xs">{n.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
